import React from 'react';
import PropTypes from 'prop-types';
import styled from 'react-emotion';

import { ConfirmationModal } from 'web-ui-components/lib/organisms/modals';
import { Divider } from 'web-ui-components/lib/atoms/layout';
import { ProminentLabel } from 'web-ui-components/lib/typography/labels';
import { Column, Row } from 'web-ui-components/lib/global/layout';
import { GenericCheckbox, GenericRadio } from 'web-ui-components/lib/data/capture';
import { SubBody } from 'web-ui-components/lib/typography/body';
import { StatusBanner, ValidationMessageTile } from 'web-ui-components/lib/layout/content';

import withContent from '../../containers/withContent';

import authoriserContent, { contentPropTypes } from './AuthoriserSelector.content';

const StyledSelectAuthorisersList = styled('div')`
	margin: 8px 16px 24px 0;
	max-height: ${(props) => (props.isSingleList ? '96px' : '123px')};
	overflow: auto;

	> div {
		margin-right: ${(props) => (props.isSingleList ? '16px' : '0')};
	}

	${(props) =>
		props.isSingleList &&
		`
		border: 1px solid #d2d2d2;
		padding: 12px 16px 0 16px;

		> div > div > div {
			margin-bottom: 12px;
		}
	`}
`;

const StyledDividerSection = styled('div')`
	margin: 42px 16px 12px 0;
`;

const authoriserMapFunction = (authoriser) => ({
	id: authoriser.id,
	labelText: authoriser.name,
});

const getAuthoriserNomenclature = (number) => (number === 1 ? 'authoriser' : 'authorisers');

class AuthoriserSelector extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			hasTriedToSelectTooManyDualAuthorisers: false,
			selectedAuthorisers: [],
			multiListActive: false,
		};

		this.handleSingleAuthoriserListChange = this.handleSingleAuthoriserListChange.bind(this);
		this.handleMultiAuthoriserListChange = this.handleMultiAuthoriserListChange.bind(this);
		this.handleConfirm = this.handleConfirm.bind(this);
		this.handleClose = this.handleClose.bind(this);
	}

	componentDidUpdate(prevProps) {
		const { multiAuthorisersList, userId } = this.props;
		const { selectedAuthorisers } = this.state;

		if (!prevProps.isOpen && this.props.isOpen && selectedAuthorisers.length === 0) {
			const userIsMultiAuth = multiAuthorisersList.find((authoriser) => authoriser.id === userId);
			if (userIsMultiAuth) {
				selectedAuthorisers.push(userIsMultiAuth);
				// eslint-disable-next-line react/no-did-update-set-state
				this.setState({ selectedAuthorisers, multiListActive: true });
			}
		}
	}

	handleSingleAuthoriserListChange(selectedId) {
		const { singleAuthorisersList } = this.props;
		const newSelectedAuthoriser = singleAuthorisersList.find((authoriser) => authoriser.id === selectedId);

		return this.setState({ selectedAuthorisers: [newSelectedAuthoriser], multiListActive: false });
	}

	handleMultiAuthoriserListChange(selectedIds) {
		const { multiAuthorisersList } = this.props;

		const DUAL_AUTHORISERS_AMOUNT = 2;
		const numberOfAuthorisers = selectedIds.length;

		if (numberOfAuthorisers > DUAL_AUTHORISERS_AMOUNT) {
			return this.setState({ hasTriedToSelectTooManyDualAuthorisers: true });
		}

		const newSelectedAuthorisers = multiAuthorisersList.filter((authoriser) => selectedIds.includes(authoriser.id));

		// Do we need this conditional, always set multiListActive to true
		if (this.state.multiListActive) {
			return this.setState({
				hasTriedToSelectTooManyDualAuthorisers: false,
				selectedAuthorisers: newSelectedAuthorisers,
			});
		}
		return this.setState({
			hasTriedToSelectTooManyDualAuthorisers: false,
			selectedAuthorisers: newSelectedAuthorisers,
			multiListActive: true,
		});
	}

	handleConfirm() {
		this.props.onConfirm(this.state.selectedAuthorisers, this.props.paymentId);
		this.props.onClose();
	}

	handleClose() {
		this.props.onClose();
		this.setState({
			selectedAuthorisers: [],
			multiListActive: false,
		});
	}

	render() {
		const { singleAuthorisersList, multiAuthorisersList, isOpen, content } = this.props;
		const { hasTriedToSelectTooManyDualAuthorisers, selectedAuthorisers, multiListActive } = this.state;

		const { selectedTooManyAuthorisers } = content;

		const confirmDisabled = multiListActive ? selectedAuthorisers.length < 2 : selectedAuthorisers.length !== 1;
		const showSingleList = singleAuthorisersList.length > 0;
		const showMultiList = multiAuthorisersList.length > 1;
		const singleListSelectedCount = selectedAuthorisers.length && !multiListActive ? selectedAuthorisers.length : 0;
		const multiListSelectedCount = selectedAuthorisers.length && multiListActive ? selectedAuthorisers.length : 0;
		const noUsers = !showMultiList && !showSingleList;

		const singleAuthoriserItems = singleAuthorisersList.map(authoriserMapFunction);
		const multiAuthoriserItems = multiAuthorisersList.map(authoriserMapFunction);

		const singleSelectedAuthoriser = singleListSelectedCount ? selectedAuthorisers[0].id : null;

		return (
			<ConfirmationModal
				titleText={noUsers ? content.noUsersTitle : content.title}
				onConfirm={this.handleConfirm}
				isOpen={isOpen}
				onClose={this.handleClose}
				dismissButtonText={content.dismissButtonLabel}
				confirmButtonText={content.confirmButtonLabel}
				confirmButtonIsDisabled={confirmDisabled}
			>
				{(showSingleList || showMultiList) && <p>{content.subText}</p>}
				{noUsers && <p>{content.noUsers}</p>}
				{showSingleList && (
					<div>
						<ProminentLabel>
							<span>
								{content.singleListHeader}
								{` (${singleAuthorisersList.length})`}
							</span>
						</ProminentLabel>
						<StatusBanner.Information>
							<SubBody isFlush>
								<span>{`You have ${singleListSelectedCount ? `` : ` not`} selected a single authoriser`}</span>
							</SubBody>
						</StatusBanner.Information>

						<StyledSelectAuthorisersList isSingleList>
							<GenericRadio.StackedSet
								groupName="single-authoriser-radio-group"
								items={singleAuthoriserItems}
								onChange={this.handleSingleAuthoriserListChange}
								selectedId={singleSelectedAuthoriser}
							/>
						</StyledSelectAuthorisersList>
					</div>
				)}
				{showMultiList && showSingleList && (
					<StyledDividerSection>
						<Row>
							<Column md={10}>
								<Divider />
							</Column>
							<Column md={4}>
								<p style={{ marginTop: '-12px' }}>
									<ProminentLabel>
										<center>OR</center>
									</ProminentLabel>
								</p>
							</Column>
							<Column md={10}>
								<Divider />
							</Column>
						</Row>
					</StyledDividerSection>
				)}
				{showMultiList && (
					<div>
						<ProminentLabel>
							<span>
								{content.multiListHeader}
								{` (${multiAuthorisersList.length})`}
							</span>
						</ProminentLabel>

						<StatusBanner.Information>
							<SubBody isFlush>
								<span>
									You have selected{' '}
									{`${multiListSelectedCount} dual ${getAuthoriserNomenclature(multiListSelectedCount)}`}
								</span>
							</SubBody>
						</StatusBanner.Information>

						{hasTriedToSelectTooManyDualAuthorisers && (
							<ValidationMessageTile messageText={selectedTooManyAuthorisers} />
						)}

						<StyledSelectAuthorisersList>
							<GenericCheckbox.StackedSet
								items={multiAuthoriserItems}
								groupName="multi-authorisers-checkboxes"
								onChange={this.handleMultiAuthoriserListChange}
								selectedIds={selectedAuthorisers.map((selectedAuthoriser) => selectedAuthoriser.id)}
							/>
						</StyledSelectAuthorisersList>
					</div>
				)}
			</ConfirmationModal>
		);
	}
}

AuthoriserSelector.propTypes = {
	singleAuthorisersList: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
	multiAuthorisersList: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
	onClose: PropTypes.func.isRequired,
	isOpen: PropTypes.bool.isRequired,
	onConfirm: PropTypes.func.isRequired,
	paymentId: PropTypes.string,
	content: PropTypes.shape(contentPropTypes),
	userId: PropTypes.string,
};

export default withContent({ content: authoriserContent })(AuthoriserSelector);
export { AuthoriserSelector };
